import React, { useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { bindActionCreators } from "redux";
import * as providerActionCreators from "../../../load-build/orders/actions";
import { useUserInfo } from "../../../common/user-context";
import { log } from "../../../Helper";
import { getUrgentActionsList } from "../../api";
import { useHistory } from 'react-router-dom';
import CardLoaderComponent from "../../../CardLoaderComponent";
import UrgentActionTile from "../../../components/UrgentActionTile";
import { URGENT_ACTION_TOOLTIPS } from "../../../constants/constants";
import _ from "lodash";

const UrgentActionOrdersWidget = () => {
  const { userData } = useUserInfo();
  const history = useHistory();
  const dispatch = useDispatch();
  const { load } = bindActionCreators(providerActionCreators, dispatch);

  // State management
  const [selectedProviderId, setSelectedProviderId] = useState("");
  const [urgentActionOrders, setUrgentActionOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lastCalledSupplier, setLastCalledSupplier] = useState(null);

  // Redux selectors
  const providerList = useSelector((storeState) => storeState.getProviderList);
  const sapSupplierNumber = useSelector((storeState) => storeState.setProvider);
  
  // Constants
  const supplierNumber = userData.supplierNumber;
  const effectiveProviderId = _.isEmpty(sapSupplierNumber) ? supplierNumber : sapSupplierNumber;
  const isInternalUser = userData.internal === true;
  const isForwarderUser = userData.internal === false && userData.forwarder === true;

  // Sort providers alphabetically
  const sortedProviderList = useMemo(
    () => providerList.sort((a, b) => a.descr.localeCompare(b.descr, "en", { sensitivity: "base" })),
    [providerList]
  );

  // Helper function for navigation with filter data
  const navigateWithFilter = (path, filterData, filterTitle, filterType = null) => {
    const navigationState = {
      filterComar: filterData,
      autoApplyFilter: true,
      filterTitle
    };
    if (filterType) {
      navigationState.filterType = filterType;
    }
    history.push(path, navigationState);
  };

  // Helper function to create buildId/COMAR mapping
  const createBuildIdComarMapping = (orders) => {
    return orders.map(order => ({
      buildId: order.buildId || 0,
      comar: order.comar
    }));
  };

  // Helper function to extract unique COMAR values
  const extractUniqueComars = (orders, comarKey = 'comar') => {
    return orders
      .map(order => order[comarKey])
      .filter(Boolean)
      .filter((comar, index, self) => self.indexOf(comar) === index);
  };

  // Load providers on component mount (only for internal users)
  useEffect(() => {
    if (isInternalUser && providerList.length === 0) {
      load();
    }
  }, [isInternalUser, providerList.length, load]);

  // Auto-load data for forwarder users (only once on mount)
  useEffect(() => {
    if (isForwarderUser && supplierNumber && lastCalledSupplier !== supplierNumber) {
      loadUrgentActionsOrders(supplierNumber);
    }
  }, [isForwarderUser, supplierNumber, lastCalledSupplier]);

  // Auto-load data for internal users if a provider is already selected in Redux
  useEffect(() => {
    if (isInternalUser && effectiveProviderId && effectiveProviderId !== supplierNumber && providerList.length > 0) {
      setSelectedProviderId(effectiveProviderId);
      // Find the provider and use its sapSupplierNumber
      const selectedProvider = providerList.find(p => p.providerId === effectiveProviderId);
      const sapSupplierNum = selectedProvider?.sapSupplierNumber;
      
      if (sapSupplierNum && lastCalledSupplier !== sapSupplierNum) {
        loadUrgentActionsOrders(sapSupplierNum);
      }
    }
  }, [isInternalUser, effectiveProviderId, supplierNumber, providerList, lastCalledSupplier]);

  // Watch for provider changes from other components (only for internal users)
  useEffect(() => {
    if (isInternalUser && sapSupplierNumber && sapSupplierNumber !== selectedProviderId && lastCalledSupplier !== sapSupplierNumber) {
      // Find provider by sapSupplierNumber and set the providerId for UI
      const matchingProvider = providerList.find(p => p.sapSupplierNumber === sapSupplierNumber);
      if (matchingProvider) {
        setSelectedProviderId(matchingProvider.providerId);
      }
      // Use the sapSupplierNumber directly from Redux
      loadUrgentActionsOrders(sapSupplierNumber);
    }
  }, [sapSupplierNumber, isInternalUser, selectedProviderId, providerList, lastCalledSupplier]);

  // Handle provider selection change (only for internal users)
  const onProviderChange = (sapSuplrNo) => {
    if (isInternalUser) {
      setSelectedProviderId(sapSuplrNo);
      if (sapSuplrNo) {
        // Set provider in Redux to sync across pages (load build, avis, home)
        dispatch({ type: 'SET_PROVIDER', payload: sapSuplrNo });
        // Send sapSuplrNo directly to API only if it's different from last called
        if (lastCalledSupplier !== sapSuplrNo) {
          loadUrgentActionsOrders(sapSuplrNo);
        }
      } else {
        // Clear provider in Redux when no selection
        dispatch({ type: 'SET_PROVIDER', payload: '' });
        setUrgentActionOrders([]);
        setLastCalledSupplier(null);
      }
    }
  };

  // Click handlers for navigation
  const handleLatePickupMachinesClick = () => {
    if (urgentActionOrders.latePickupMachines?.length > 0) {
      const comarList = extractUniqueComars(urgentActionOrders.latePickupMachines, 'comarno');
      navigateWithFilter('/forwarder/load-build', comarList, 'LATE PICKUP');
    }
  };

  const handleLatePickupAvisClick = () => {
    if (urgentActionOrders.latePickupAvisOrders?.length > 0) {
      const buildIdComarList = createBuildIdComarMapping(urgentActionOrders.latePickupAvisOrders);
      navigateWithFilter('/forwarder/avis', buildIdComarList, 'LATE PICKUP', 'buildIdComar');
    }
  };

  const handleDeliveryDateMissingClick = () => {
    if (urgentActionOrders.deliveryDateMissing?.length > 0) {
      const buildIdComarList = createBuildIdComarMapping(urgentActionOrders.deliveryDateMissing);
      navigateWithFilter('/forwarder/avis', buildIdComarList, 'DELIVERY DATE MISSING', 'buildIdComar');
    }
  };

  const handleVatDeclarationMissingClick = () => {
    if (urgentActionOrders.vatDeclarationMissing?.length > 0) {
      const buildIdComarList = createBuildIdComarMapping(urgentActionOrders.vatDeclarationMissing);
      navigateWithFilter('/forwarder/avis', buildIdComarList, 'VAT DECLARATION MISSING', 'buildIdComar');
    }
  };

  // Load urgent actions for selected provider
  const loadUrgentActionsOrders = async (sapSupplierNumber) => {
    try {
      if (lastCalledSupplier === sapSupplierNumber) return;
      
      setIsLoading(true);
      setLastCalledSupplier(sapSupplierNumber);
      log("Loading urgent actions for supplier:", sapSupplierNumber);
      
      const response = await getUrgentActionsList(sapSupplierNumber);

      if (response) {
        // Construct urgent actions data
        const urgentActionsData = {
          latePickupMachines: response.latePickupLoadbuildOrders || [],
          deliveryDateMissing: response.deliveryDateMissingOrders || [],
          vatDeclarationMissing: response.vatMissingOrders || [],
          latePickupAvisOrders: response.latePickupAvisOrders || []
        };
        
        setUrgentActionOrders(urgentActionsData);
        log("Successfully loaded urgent actions:", urgentActionsData);
      } else {
        log("No urgent actions data received");
        setUrgentActionOrders({});
      }
    } catch (error) {
      log("Error loading urgent actions:", error);
      setUrgentActionOrders({});
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to calculate unique buildIds count
  const getUniqueBuildIdCount = (orders) => {
    const uniqueBuildIds = new Set(
      orders
        .map(order => order.buildId)
        .filter(buildId => buildId !== null && buildId !== undefined)
    );
    return uniqueBuildIds.size;
  };

  // Helper function to calculate VAT declaration count (unique buildIds + null buildId orders)
  const getVatDeclarationCount = (orders) => {
    if (!orders || orders.length === 0) return 0;
    
    const uniqueBuildIds = new Set(
      orders
        .map(order => order.buildId)
        .filter(buildId => buildId !== null && buildId !== undefined)
    );
    
    const nullBuildIdCount = orders.filter(order => order.buildId === null || order.buildId === undefined).length;
    
    return uniqueBuildIds.size + nullBuildIdCount;
  };

  return (
    <div className="w-full">
      {/* Widget Title with Forwarder Select */}
      <div className="mb-4 flex justify-between items-center">
        <h2 className="text-lg font-semibold text-gray-800">Urgent Action Orders</h2>
        
        {/* Forwarder Select - Only show for internal users */}
        {isInternalUser && (
          <select
            className="text-sm border border-gray-300 rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-60 disabled:bg-gray-100 disabled:cursor-not-allowed"
            value={selectedProviderId}
            onChange={(evt) => onProviderChange(evt.target.value)}
            disabled={isLoading}
          >
            <option value="">Select Forwarder</option>
            {sortedProviderList.map((providerOption) => (
              <option
                key={providerOption.providerId}
                value={providerOption.sapSuplrNo}
              >
                {providerOption.descr}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Content Area */}
      <div className="min-h-[200px]">
        {isLoading ? (
          <CardLoaderComponent
            desc="urgent action orders"
            subtitle="This might take a while"
          />
        ) : (!selectedProviderId && isInternalUser) ? (
          <div className="flex justify-center items-center h-[200px]">
            <div className="text-gray-500">Please select a forwarder to view urgent action orders</div>
          </div>
        ) : !urgentActionOrders || Object.keys(urgentActionOrders).length === 0 ? (
          <div className="flex justify-center items-center h-[200px]">
            <div className="text-gray-500">No urgent action orders found for selected forwarder</div>
          </div>
        ) : (
          /* Urgent Actions Summary */
          <div className="space-y-2">
            <UrgentActionTile
              title="Missing Shipping Order"
              tooltip={URGENT_ACTION_TOOLTIPS.MISSING_SHIPPING_ORDER}
              count={urgentActionOrders.latePickupMachines?.length || 0}
              onClick={handleLatePickupMachinesClick}
              isActive={(urgentActionOrders.latePickupMachines?.length || 0) > 0}
            />

            <UrgentActionTile
              title="Late Pickup"
              tooltip={URGENT_ACTION_TOOLTIPS.LATE_PICKUP}
              count={urgentActionOrders.latePickupAvisOrders?.length || 0}
              onClick={handleLatePickupAvisClick}
              isActive={(urgentActionOrders.latePickupAvisOrders?.length || 0) > 0}
            />

            <UrgentActionTile
              title="Delivery Date Missing"
              tooltip={URGENT_ACTION_TOOLTIPS.DELIVERY_DATE_MISSING}
              count={getUniqueBuildIdCount(urgentActionOrders.deliveryDateMissing || [])}
              onClick={handleDeliveryDateMissingClick}
              isActive={(urgentActionOrders.deliveryDateMissing?.length || 0) > 0}
            />

            <UrgentActionTile
              title="VAT Declaration Missing"
              tooltip={URGENT_ACTION_TOOLTIPS.VAT_DECLARATION_MISSING}
              count={getVatDeclarationCount(urgentActionOrders.vatDeclarationMissing || [])}
              onClick={handleVatDeclarationMissingClick}
              isActive={(urgentActionOrders.vatDeclarationMissing?.length || 0) > 0}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default UrgentActionOrdersWidget;
